import { imageSpaceAPI, taskAPI } from '@/utils/api';
import { isManagedStorageUrl } from '@/utils/imageDisplayAccess';
import { getImg2ImgOutputs, normalizeImg2ImgOutputRow } from './img2imgTaskResult';
import {
  isExternalGeneratedResultUrl,
  mergeIngestedResultsIntoStudioSuccessResult,
  resultHasExternalOutputUrls,
} from './aiStudioImageTaskRecord';
import {
  buildSmartToolResultRecordId,
  persistSmartToolResultsToImageSpace,
} from './persistSmartToolResultsToImageSpace';

const PERSISTED_OUTPUT_NAV_IDS = new Set([
  'imageCrop',
  'imageEnhance',
  'smartCutout',
  'stampExtract',
  'aiOutpaint',
  'aiTextToImage',
  'img2img',
  'imageFission',
  'patternFission',
  'modelTryOn',
  'modelHandheld',
  'modelPoseFission',
  'modelUniversalWear',
  'goods',
]);

function outputNeedsPersistedUrl(output) {
  const url = String(output?.outputUrl || output?.url || output?.imageUrl || '').trim();
  // 空 URL 无法转存；若仍为 true，失败行会反复 getById → setTasks 卡死浏览器
  if (!url) return false;
  if (url.startsWith('data:') || url.startsWith('blob:')) return false;
  if (String(output?.imageSpaceId || output?.spaceId || '').trim()) return false;
  if (isExternalGeneratedResultUrl(url) || !isManagedStorageUrl(url)) return true;
  // 已是受管 OSS URL 但 compat 元数据缺失时，仍尝试按 recordId 补登记
  return true;
}

/** 历史列表预览：是否仍需走图片空间补全（避免每行一次 getById） */
export function taskNeedsStudioPreviewEnrich(task) {
  if (!task) return false;
  if (task.previewViewUrl || task.previewDisplayUrl) return false;
  const result = task.result && typeof task.result === 'object' ? task.result : {};
  const outputs = getImg2ImgOutputs(result);
  const first = outputs[0];
  if (!first) return resultHasExternalOutputUrls(result);
  return outputNeedsPersistedUrl(first);
}

async function resolvePersistedOutput(navId, taskId, index, output) {
  const rawUrl = String(output?.outputUrl || output?.url || output?.imageUrl || '').trim();
  if (isExternalGeneratedResultUrl(rawUrl)) {
    try {
      const saved = await persistSmartToolResultsToImageSpace({
        navId,
        taskId,
        urls: [rawUrl],
        outputs: [output],
      });
      if (saved.length) {
        const merged = mergeIngestedResultsIntoStudioSuccessResult(
          { outputs: [normalizeImg2ImgOutputRow(output, index)].filter(Boolean) },
          saved,
          { navId, taskId },
        );
        return merged.outputs?.[0] || null;
      }
    } catch (error) {
      console.warn('[图片空间] 外链结果转存失败', navId, taskId, error);
    }
  }

  const recordId = buildSmartToolResultRecordId(navId, taskId, index, output);
  try {
    const res = await imageSpaceAPI.getById(recordId);
    const img = res?.image || res?.data || res;
    const url = String(img?.url || img?.src || '').trim();
    if (!url) return null;
    const viewUrl = String(
      img?.viewUrl || img?.storedViewUrl || img?.displayUrl || img?.urlDisplay || '',
    ).trim();
    return normalizeImg2ImgOutputRow({
      ...output,
      url,
      outputUrl: url,
      imageUrl: url,
      viewUrl,
      displayUrl: viewUrl || String(img?.displayUrl || img?.urlDisplay || '').trim(),
      imageSpaceId: String(img?.id || recordId).trim(),
      name: String(img?.name || output?.name || '').trim() || output?.name,
      status: output?.status || 'success',
      index: output?.index ?? index,
      sourceIndex: output?.sourceIndex ?? index,
    }, index);
  } catch {
    if (isManagedStorageUrl(rawUrl)) {
      try {
        const saved = await persistSmartToolResultsToImageSpace({
          navId,
          taskId,
          urls: [rawUrl],
          outputs: [output],
        });
        if (saved.length) {
          const merged = mergeIngestedResultsIntoStudioSuccessResult(
            { outputs: [normalizeImg2ImgOutputRow(output, index)].filter(Boolean) },
            saved,
            { navId, taskId },
          );
          return merged.outputs?.[0] || null;
        }
      } catch (error) {
        console.warn('[图片空间] 受管 URL 补登记失败', navId, taskId, error);
      }
    }
    return null;
  }
}

/**
 * 历史任务 result.outputs 可能仍存灵图临时外链；从图片空间按 recordId 补全持久化地址。
 */
export async function enrichStudioTaskPersistedOutputs(task, navId, { previewOnly = false, writeBack = false } = {}) {
  const toolNavId = String(navId || '').trim();
  if (!task || !toolNavId) return task;
  const taskId = String(task.id || '').trim();
  if (!taskId) return task;

  const result = task.result && typeof task.result === 'object' ? task.result : {};
  const outputs = getImg2ImgOutputs(result);
  const hasExternalUrls = resultHasExternalOutputUrls(result);
  if (!outputs.length && !hasExternalUrls) return task;
  if (!PERSISTED_OUTPUT_NAV_IDS.has(toolNavId) && !hasExternalUrls) return task;

  const indicesToFix = (previewOnly ? [{ output: outputs[0], index: 0 }] : outputs.map((output, index) => ({ output, index })))
    .filter(({ output }) => output && outputNeedsPersistedUrl(output));
  if (!indicesToFix.length) return task;

  const nextOutputs = [...outputs];
  let changed = false;
  await Promise.all(indicesToFix.map(async ({ output, index }) => {
    const patched = await resolvePersistedOutput(toolNavId, taskId, index, output);
    if (!patched) return;
    nextOutputs[index] = patched;
    changed = true;
  }));

  if (!changed) return task;

  const urls = nextOutputs
    .map((row) => String(row?.outputUrl || row?.url || '').trim())
    .filter(Boolean);
  const first = nextOutputs.find((row) => row?.outputUrl || row?.url) || nextOutputs[0];
  const previewUrl = String(first?.outputUrl || first?.url || urls[0] || task.previewUrl || '').trim();
  const previewViewUrl = String(first?.viewUrl || first?.displayUrl || task.previewViewUrl || '').trim();

  const enriched = {
    ...task,
    previewUrl,
    previewViewUrl,
    previewDisplayUrl: previewViewUrl || task.previewDisplayUrl || '',
    result: {
      ...result,
      outputs: nextOutputs,
      urls: urls.length ? urls : result.urls,
      previewUrl,
    },
  };

  if (writeBack && taskId) {
    try {
      await taskAPI.update(taskId, {
        previewUrl,
        result: enriched.result,
      });
    } catch (error) {
      console.warn('[任务] 内部化结果回写失败', taskId, error);
    }
  }

  return enriched;
}
